import { Stack, Typography } from '@mui/material';
import { ComponentProps, JSXElementConstructor, useMemo } from 'react';

import { DocumentDetails_DocumentSummaryFragment } from '@/modules/documents/graphql/DocumentDetails.generated';
import { getDocumentAttributionText } from '@/utils/formatting/documentAttribution';

export interface DocumentMetadataSlots<
  RateSummaryProps = Record<string, unknown>,
> {
  RateSummary?: JSXElementConstructor<RateSummaryProps>;
  RateSummaryProps?: RateSummaryProps;
}

interface DocumentMetadataProps<RateSummaryProps = Record<string, unknown>> {
  documentSummary: DocumentDetails_DocumentSummaryFragment;
  stackSx?: ComponentProps<typeof Stack>['sx'];
  slots?: DocumentMetadataSlots<RateSummaryProps>;
}

export function DocumentMetadata<RateSummaryProps = Record<string, unknown>>({
  documentSummary,
  stackSx,
  slots,
}: DocumentMetadataProps<RateSummaryProps>) {
  const createdOrEditedBy = useMemo(() => {
    if (!documentSummary) return '';

    const entryMode =
      documentSummary.entryMode === undefined
        ? null
        : documentSummary.entryMode;

    return getDocumentAttributionText({
      entryMode,
      updateUser: documentSummary.updateUser,
      updatedAt: documentSummary.updatedAt,
      includeDate: true,
      useSummaryCompleteLabel: true,
    });
  }, [documentSummary]);

  if (!documentSummary) return null;

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={stackSx}
      >
        <Typography variant="body-xs">{createdOrEditedBy}</Typography>
        <Stack direction="row" spacing={2}>
          {slots?.RateSummary && slots.RateSummaryProps && (
            <slots.RateSummary {...slots.RateSummaryProps} />
          )}
        </Stack>
      </Stack>
    </>
  );
}
